// ============================================================================
// PAYMENT MODE SERVICE (ADR-0055 §6) — the company-wide payment modes every
// capture screen offers (stored in instrument_type), and the charge rules per
// entity / bank account that drive the reconciliation's charge suggestions.
// ============================================================================

import { del, get, post, put } from '../client/axiosClient';
import { paymentModeEndpoints } from '../endpoints/banking.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, ID, PaymentModeDto, SavePaymentModeRequest, ChargeRuleDto, SaveChargeRuleRequest, ChargeRateStatDto,
} from '../types';

let modesCache: Promise<PaymentModeDto[]> | null = null;

class PaymentModeService {
  /** The mode list (cached for the session; cleared on save). */
  async getModes(force = false): Promise<PaymentModeDto[]> {
    if (!modesCache || force) {
      modesCache = get<ApiResponse<PaymentModeDto[]>>(paymentModeEndpoints.list())
        .then(r => r.data.data ?? [])
        .catch(err => { modesCache = null; throw err; });
    }
    return modesCache;
  }

  async saveMode(id: ID | null, data: SavePaymentModeRequest): Promise<PaymentModeDto> {
    const response = id
      ? await put<ApiResponse<PaymentModeDto>>(paymentModeEndpoints.update(id), data)
      : await post<ApiResponse<PaymentModeDto>>(paymentModeEndpoints.list(), data);
    modesCache = null;
    return response.data.data;
  }

  /** Entity defaults and, given a bank account, its overrides. */
  async getRules(bankLedgerId?: ID): Promise<ChargeRuleDto[]> {
    const endpoint = bankLedgerId ? buildEndpointWithQuery(paymentModeEndpoints.rules(), { bankLedgerId }) : paymentModeEndpoints.rules();
    const response = await get<ApiResponse<ChargeRuleDto[]>>(endpoint);
    return response.data.data ?? [];
  }

  async saveRule(id: ID | null, data: SaveChargeRuleRequest): Promise<ChargeRuleDto> {
    const response = id
      ? await put<ApiResponse<ChargeRuleDto>>(paymentModeEndpoints.rule(id), data)
      : await post<ApiResponse<ChargeRuleDto>>(paymentModeEndpoints.rules(), data);
    return response.data.data;
  }

  async deleteRule(id: ID): Promise<void> {
    await del<ApiResponse<unknown>>(paymentModeEndpoints.rule(id));
  }

  /** The rate actually paid per mode on the bank account over the last `days` days. */
  async getChargeStats(bankLedgerId: ID, days = 90): Promise<ChargeRateStatDto[]> {
    const response = await get<ApiResponse<ChargeRateStatDto[]>>(buildEndpointWithQuery(paymentModeEndpoints.stats(), { bankLedgerId, days }));
    return response.data.data ?? [];
  }
}

export const paymentModeService = new PaymentModeService();
export default paymentModeService;
